/**
 * Per-tenant Topology page (§16 V1).
 *
 * Aggregated mesh across all the tenant's scans, grouped by repo with
 * agents deduped by `agent_id_hint`. The mesh is built server-side
 * (`GET /tenants/{tenant_id}/mesh`) and rendered by the shared
 * MeshView component.
 */
import { useEffect, useState } from 'react'
import { Alert, Box, Button, Chip, Stack, Typography } from '@mui/material'
import { AccountTree } from '@mui/icons-material'
import { useSelector } from 'react-redux'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { analysisApi } from '../services/api'
import type { RootState } from '../store'
import { MeshView, NodeDetailDrawer } from '../components/MeshView'
import type { MeshNode, MeshResponse, OverlayMode } from '../components/MeshView/types'

export default function TopologyPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  // AR3 — `?focus=<agent_id_hint>` auto-zooms the canvas into that
  // node's neighborhood on mount. Used by the registry detail dialog's
  // "View in topology" button.
  const focusNodeId = searchParams.get('focus')
  const user = useSelector((s: RootState) => s.auth.user)
  const tenantId = user?.tenant_id

  const [mesh, setMesh] = useState<MeshResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedNode, setSelectedNode] = useState<MeshNode | null>(null)
  // Empty means all repositories, so the default needs no special case.
  const [repoFilter, setRepoFilter] = useState<string[]>([])
  const [reloadKey, setReloadKey] = useState(0)
  // M5b — Compliance Lens state
  const [overlay, setOverlay] = useState<OverlayMode>('risk')
  const [complianceFramework, setComplianceFramework] = useState<string | null>(null)
  const [availableFrameworks, setAvailableFrameworks] = useState<Array<{ key: string; display_name?: string }>>([])
  // M7a — Drift timeline scrubber state
  const [timelineScans, setTimelineScans] = useState<Array<{ at: string; repo_id: string }>>([])
  const [scrubberAt, setScrubberAt] = useState<string | null>(null)  // null = live (latest)

  useEffect(() => {
    if (!tenantId) return
    let cancelled = false
    setLoading(true)
    setError(null)
    const params: { lens?: string; framework?: string; at?: string } = {}
    if (overlay === 'compliance' && complianceFramework) {
      params.lens = 'compliance'
      params.framework = complianceFramework
    }
    if (scrubberAt) params.at = scrubberAt
    analysisApi
      .getTenantMesh(tenantId, params)
      .then((res) => {
        if (!cancelled) setMesh(res.data as MeshResponse)
      })
      .catch((e: unknown) => {
        if (cancelled) return
        const detail =
          (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
          (e as { message?: string })?.message ||
          'Failed to load topology'
        setError(detail)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [tenantId, reloadKey, overlay, complianceFramework, scrubberAt])

  // Load timeline scans for the scrubber once per tenant
  useEffect(() => {
    if (!tenantId) return
    let cancelled = false
    analysisApi
      .getTenantMeshTimeline(tenantId)
      .then((res) => {
        if (!cancelled) setTimelineScans(res.data?.scans || [])
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [tenantId, reloadKey])

  // Load compliance framework list once for this tenant
  useEffect(() => {
    let cancelled = false
    analysisApi
      .getComplianceFrameworks()
      .then((res) => {
        if (cancelled) return
        const list = (res.data?.frameworks || []) as Array<{ framework: string; display_name?: string }>
        setAvailableFrameworks(list.map((f) => ({ key: f.framework, display_name: f.display_name })))
      })
      .catch(() => {
        // Compliance Posture may not be set up for this tenant — just leave list empty.
      })
    return () => {
      cancelled = true
    }
  }, [tenantId])

  if (!tenantId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">No tenant in session — please log in again.</Alert>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 2, height: 'calc(100vh - 80px)', display: 'flex', flexDirection: 'column' }}>
      <Stack spacing={0.5} sx={{ mb: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Topology
          </Typography>
          <Chip
            icon={<AccountTree sx={{ fontSize: 14 }} />}
            label={`All your scans${
              mesh?.meta?.repo_count
                ? ` · ${mesh.meta.repo_count} repo${mesh.meta.repo_count > 1 ? 's' : ''}`
                : ''
            }`}
            size="small"
            color="primary"
            sx={{ fontSize: '0.7rem', height: 22 }}
          />
        </Stack>
        <Typography variant="body2" color="text.secondary">
          A view of every agent ARIA has detected across your scans, with delegation,
          MCP, A2A linkages, and the models / tools / memory / external APIs each agent
          depends on. Click a node for details and annotations.
        </Typography>
      </Stack>

      {/* M7a — Drift timeline scrubber. Visible only when there's more
          than one scan; with a single scan there's nothing to scrub. */}
      {timelineScans.length > 1 && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, p: 1, border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'grey.50' }}>
          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
            View as of:
          </Typography>
          <input
            type="range"
            min={0}
            max={timelineScans.length - 1}
            value={scrubberAt
              ? Math.max(0, timelineScans.findIndex((s) => s.at === scrubberAt))
              : timelineScans.length - 1}
            onChange={(e) => {
              const idx = Number(e.target.value)
              const at = timelineScans[idx]?.at
              // If user moves to the latest tick, switch back to "live" mode
              if (idx === timelineScans.length - 1) setScrubberAt(null)
              else setScrubberAt(at || null)
            }}
            style={{ flex: 1, height: 4 }}
          />
          <Typography variant="caption" sx={{ minWidth: 180, color: 'text.secondary' }}>
            {scrubberAt
              ? new Date(scrubberAt).toLocaleString()
              : 'Latest (live)'}
          </Typography>
          {scrubberAt && (
            <Button size="small" onClick={() => setScrubberAt(null)} sx={{ textTransform: 'none', minWidth: 0, px: 1 }}>
              Back to live
            </Button>
          )}
        </Stack>
      )}

      <Box sx={{ flex: 1, minHeight: 400, border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
        <MeshView
          mesh={mesh || EMPTY_MESH}
          loading={loading}
          error={error}
          height="100%"
          repoFilter={repoFilter}
          onRepoFilterChange={setRepoFilter}
          onNodeClick={setSelectedNode}
          overlay={overlay}
          onOverlayChange={setOverlay}
          complianceFramework={complianceFramework}
          availableFrameworks={availableFrameworks}
          onComplianceFrameworkChange={setComplianceFramework}
          initialFocusNodeId={focusNodeId}
        />
      </Box>

      <NodeDetailDrawer
        node={selectedNode}
        onClose={() => setSelectedNode(null)}
        onAnnotationSaved={() => setReloadKey((k) => k + 1)}
        overlay={overlay}
        nodes={mesh?.nodes || []}
        edges={mesh?.edges || []}
        complianceFramework={complianceFramework}
        onOpenCertTimeline={(certId) => {
          setSelectedNode(null)
          navigate(`/certificates/${certId}`)
        }}
      />
    </Box>
  )
}

const EMPTY_MESH: MeshResponse = {
  scope: 'tenant',
  scope_id: '',
  nodes: [],
  edges: [],
  repos: [],
  meta: {
    scan_count: 0,
    agent_count: 0,
    synthetic_count: 0,
    edge_count: 0,
    annotations_count: 0,
    repo_count: 0,
  },
}
